// RND-8: adaptive canvas grid (1-2-5 × 10^n mm, minor ≥ 8 px, major every 10 minors)
// plus origin cross in axis colors. M0 uses the 2D path (WebGL image layer arrives in M4 — EC-ARC-1
// fallback is this same code).
import type { Camera } from "./camera";
import { screenToWorld, worldToScreen } from "./camera";

export interface BgTheme {
  canvas: string;
  gridMinor: string;
  gridMajor: string;
  axisX: string;
  axisY: string;
}

const STEPS_125 = [1, 2, 5];

/** Smallest 1-2-5×10^n step whose screen size is ≥ minPx. */
export function gridStep(scale: number, minPx = 8): number {
  for (let exp = -4; exp <= 9; exp++) {
    for (const s of STEPS_125) {
      const step = s * 10 ** exp;
      if (step * scale >= minPx) return step;
    }
  }
  return 10 ** 9;
}

export function renderBg(
  ctx: CanvasRenderingContext2D,
  cssW: number,
  cssH: number,
  dpr: number,
  cam: Camera,
  theme: BgTheme,
  gridVisible: boolean
): void {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = theme.canvas;
  ctx.fillRect(0, 0, cssW, cssH);
  if (!gridVisible) {
    drawOrigin(ctx, cssW, cssH, cam, theme);
    return;
  }

  const minor = gridStep(cam.scale);
  const major = minor * 10;
  const topLeft = screenToWorld(cam, cssW, cssH, { x: 0, y: 0 });
  const bottomRight = screenToWorld(cam, cssW, cssH, { x: cssW, y: cssH });

  for (const [step, color] of [
    [minor, theme.gridMinor],
    [major, theme.gridMajor]
  ] as const) {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const x0 = Math.floor(topLeft.x / step) * step;
    for (let x = x0; x <= bottomRight.x; x += step) {
      const sx = Math.round((x - cam.cx) * cam.scale + cssW / 2) + 0.5;
      ctx.moveTo(sx, 0);
      ctx.lineTo(sx, cssH);
    }
    const y0 = Math.floor(topLeft.y / step) * step;
    for (let y = y0; y <= bottomRight.y; y += step) {
      const sy = Math.round((y - cam.cy) * cam.scale + cssH / 2) + 0.5;
      ctx.moveTo(0, sy);
      ctx.lineTo(cssW, sy);
    }
    ctx.stroke();
  }

  drawOrigin(ctx, cssW, cssH, cam, theme);
}

/** Origin cross at world (0,0), 20 px arms, axis colors (RND-8). */
function drawOrigin(
  ctx: CanvasRenderingContext2D,
  cssW: number,
  cssH: number,
  cam: Camera,
  theme: BgTheme
): void {
  const o = worldToScreen(cam, cssW, cssH, { x: 0, y: 0 });
  if (o.x < -20 || o.x > cssW + 20 || o.y < -20 || o.y > cssH + 20) return;
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = theme.axisX;
  ctx.beginPath();
  ctx.moveTo(o.x - 20, o.y);
  ctx.lineTo(o.x + 20, o.y);
  ctx.stroke();
  ctx.strokeStyle = theme.axisY;
  ctx.beginPath();
  ctx.moveTo(o.x, o.y - 20);
  ctx.lineTo(o.x, o.y + 20);
  ctx.stroke();
}
